
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateBlogDialog } from '@/components/blog/CreateBlogDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  created_at: string;
  image_url?: string;
}

const Blog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BlogPost[];
    }
  });

  const handlePostClick = (postId: string) => {
    navigate(`/blog/${postId}`);
  };

  if (isLoading) {
    return (
      <div className="container py-8 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Blog</h1>
          {user && <CreateBlogDialog />}
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-[200px] w-full mb-4" />
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Blog</h1>
        {user && <CreateBlogDialog />}
      </div>
      {!posts || posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-lg text-gray-400">No blog posts available yet.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <Card 
              key={post.id} 
              className="overflow-hidden bg-black/20 border-flux-cyan/20 cursor-pointer hover:border-flux-cyan/40 transition-colors"
              onClick={() => handlePostClick(post.id)}
            >
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <h2 className="text-xl font-semibold mb-2 text-white">{post.title}</h2>
                <p className="text-gray-400 line-clamp-3">{post.excerpt}</p>
                <div className="mt-4">
                  <time className="text-sm text-gray-500">
                    {new Date(post.created_at).toLocaleDateString()}
                  </time>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Blog;
